import { Map as MapIcon, Satellite } from 'lucide-react'
import { cn } from 'lib/utils'
import type { Basemap } from '../config/mapConfig'

export interface BasemapSwitcherProps {
	value: Basemap
	onChange: (basemap: Basemap) => void
	className?: string
}

const OPTIONS: { value: Basemap; label: string; icon: typeof MapIcon }[] = [
	{ value: 'street', label: 'Map', icon: MapIcon },
	{ value: 'satellite', label: 'Satellite', icon: Satellite },
]

/**
 * Map / Satellite basemap toggle.
 *
 * Chrome matches MapControls (`bg-white/95 backdrop-blur`, subtle border) so it
 * reads as part of the map furniture rather than page content, and stays legible
 * over both a pale street map and dark imagery.
 *
 * Rendered by MapContainer rather than by each surface, so all nine maps get it
 * without nine call sites to keep in sync. MapContainer only mounts it when a
 * satellite key is configured.
 */
export function BasemapSwitcher({ value, onChange, className }: BasemapSwitcherProps) {
	return (
		<div
			className={cn(
				'inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm p-0.5',
				className,
			)}
		>
			{OPTIONS.map(({ value: v, label, icon: Icon }) => {
				const active = value === v
				return (
					<button
						key={v}
						type="button"
						onClick={() => onChange(v)}
						aria-pressed={active}
						className={cn(
							'inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
							active
								? 'bg-[#0080fe] text-white'
								: 'text-gray-600 hover:bg-gray-100',
						)}
					>
						<Icon className="h-3.5 w-3.5" />
						{label}
					</button>
				)
			})}
		</div>
	)
}

export default BasemapSwitcher
